'use client';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, MapPin, Calendar } from 'lucide-react';
import { experienceApi } from '@/lib/api';
import { Experience as ExperienceType, Education } from '@/types';

export default function Experience() {
  const [experiences, setExperiences] = useState<ExperienceType[]>([]);
  const [education, setEducation] = useState<Education[]>([]);
  const [activeTab, setActiveTab] = useState<'work' | 'education'>('work');

  useEffect(() => {
    experienceApi.getAll().then(setExperiences).catch(() => {});
    experienceApi.getEducation().then(setEducation).catch(() => {});
  }, []);



  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Present';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
  };

  return (
    <section id="experience" className="section" style={{ background: 'var(--bg-surface)' }}>
      <div className="container">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="accent-line" />
          <h2 className="section-title">Experience & Education</h2>
          <p className="section-subtitle">My journey so far</p>
        </motion.div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0', marginBottom: '2.5rem', background: 'var(--bg-card)', borderRadius: '10px', padding: '4px', width: 'fit-content', border: '1px solid var(--border)' }}>
          {(['work', 'education'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.6rem 1.5rem', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: activeTab === tab ? 'var(--accent)' : 'transparent',
                color: activeTab === tab ? '#FAF0E6' : 'var(--text-muted)',
                fontFamily: 'Outfit, sans-serif', fontWeight: 500, fontSize: '0.9rem',
                transition: 'all 0.2s ease', display: 'flex', alignItems: 'center', gap: '0.5rem',
              }}
            >
              {tab === 'work' ? <Briefcase size={15} /> : <GraduationCap size={15} />}
              {tab === 'work' ? 'Work' : 'Education'}
            </button>
          ))}
        </div>

        {/* Timeline */}
        <div style={{ position: 'relative', paddingLeft: '2rem' }}>
          {(activeTab === 'work' ? experiences : education).length === 0 ? (
            <div style={{ color: 'var(--text-muted)', padding: '2rem 0' }}>No entries found. Add them from the Django admin.</div>
          ) : (
            <>
              <div style={{ position: 'absolute', left: '0', top: 0, bottom: 0, width: '2px', background: 'linear-gradient(180deg, var(--accent), transparent)' }} />
              {(activeTab === 'work' ? experiences : education).map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  style={{ position: 'relative', marginBottom: '2rem' }}
                >
                  <div style={{ position: 'absolute', left: '-2.45rem', top: '1.25rem', width: '14px', height: '14px', borderRadius: '50%', background: 'var(--accent)', border: '3px solid var(--bg-primary)', boxShadow: '0 0 0 3px var(--accent-glow)' }} />
                  <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div>
                        <h3 style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700, fontSize: '1.05rem', marginBottom: '0.2rem' }}>
                          {'role' in item ? item.role : `${item.degree} in ${item.field}`}
                        </h3>
                        <p style={{ color: 'var(--accent)', fontWeight: 600, fontSize: '0.95rem' }}>
                          {'company' in item ? item.company : item.institution}
                        </p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                          <Calendar size={13} />
                          {formatDate(item.start_date)} — {formatDate(item.end_date)}
                        </div>
                        {item.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'var(--text-subtle)', fontSize: '0.8rem', marginTop: '0.2rem', justifyContent: 'flex-end' }}>
                            <MapPin size={12} /> {item.location}
                          </div>
                        )}
                      </div>
                    </div>
                    {item.current && <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', background: 'var(--accent)', color: '#FAF0E6', borderRadius: '12px', fontSize: '0.75rem', marginBottom: '0.5rem' }}>Current</span>}
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7 }}>{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
